import { LoadingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { useMemo } from 'react';
import { AxisOptions, Chart } from 'react-charts';

dayjs.extend(isSameOrAfter);

type ChartDatum = { date: string; value: number };
type ParsedDatum = { date: Date; value: number; label: string };

type ChartWithRangeSelectorProps = {
  data: ChartDatum[];
  range: number;
  onRangeChange: (range: number) => void;
  loading?: boolean;
};

export default function ChartWithRangeSelector({
  data,
  range,
  onRangeChange,
  loading = false,
}: ChartWithRangeSelectorProps) {
  const parsedData = useMemo<ParsedDatum[]>(
    () =>
      (data || []).map((item) => ({
        date: new Date(item.date),
        value: item.value,
        label: dayjs(item.date).format('MMM D'),
      })),
    [data]
  );

  const chartData = useMemo(
    () => [
      {
        label: 'Entries',
        data: parsedData,
      },
    ],
    [parsedData]
  );

  const primaryAxis = useMemo<AxisOptions<ParsedDatum>>(
    () => ({
      getValue: (datum) => datum.label,
      scaleType: 'band',
    }),
    []
  );

  const secondaryAxes = useMemo<AxisOptions<ParsedDatum>[]>(
    () => [
      {
        getValue: (datum) => datum.value,
        elementType: 'bar',
      },
    ],
    []
  );

  const ranges = [7, 15, 30, 60];

  return (
    <div style={{ padding: 20 }}>
      <div style={{ marginBottom: 10 }}>
        {ranges.map((r) => (
          <button
            key={r}
            onClick={() => onRangeChange(r)}
            style={{
              marginRight: 10,
              padding: '6px 12px',
              background: range === r ? '#3DCCC7' : '#eee',
              color: range === r ? '#fff' : '#000',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
            }}
          >
            Last {r} Days
          </button>
        ))}
      </div>

      <div style={{ height: 300 }}>
        {loading ? (
          <LoadingOutlined
            style={{
              fontSize: 44,
              display: 'flex',
              height: '100%',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          />
        ) : parsedData.length === 0 ? (
          <p>No data available</p>
        ) : (
          <Chart
            options={{
              data: chartData,
              primaryAxis,
              secondaryAxes,
              tooltip: true,
              getSeriesStyle: () => ({
                color: '#1890ff',
              }),
            }}
          />
        )}
      </div>
    </div>
  );
}
